import { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { apiUrls, routeURL } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { Tooltip } from '@mui/material';
import {
  SummaryContainer,
  ScrollableContent,
  LoadingBox,
  LoadingText,
  NoDataBox,
  NoDataTitle,
  NoDataText,
  TopRowWrapper,
  LeftColumn,
  SectionTitle,
  RowBetween,
  ColumnGap8,
  ColumnGap8Right,
  DateBlock,
  AddressCard,
  AddressTitle,
  AddressRow,
  AddressName,
  AddressLine,
  AddressLinesWrap,
  AddressesWrap,
  ItemsTitle,
  ItemsContainer,
  ItemsListContainer,
  ItemsScrollWrapper,
  ItemsHeader,
  ItemRow,
  ItemCell2,
  ItemCell1Right,
  SmallLabel14,
  SmallLabel14Right,
  SmallValue14,
  StyledBusinessIcon,
  StyledCheckCircleIcon,
  RightColumn,
  DiscountsHeaderWrap,
  DiscountsRow,
  DiscountAppliedRow,
  DiscountText,
  DiscountAmountLabel,
  SurchargesBlock,
  SurchargesTitle,
  SurchargeRow,
  SurchargeText,
  SurchargeValue,
  FinalTotalsHeading,
  TotalsRow,
  TotalsRowMb2373,
  TotalsLabel,
  TotalsValueCombined,
  SurchargeLabelBlue,
  SurchargesValue,
  GrandTotalBox,
  ActionsWrap,
  BackButton,
  SaveButton,
  SaveCompleteButton,
  DateBlockContainer,
  TooltipContainer,
  TooltipTitle,
  TooltipRow,
  TooltipLabel,
  TooltipValue,
  TooltipTotalRow,
  TooltipTotalLabel,
  TooltipTotalValue,
  InfoIconStyled,
  InfoIconBlue,
  LabelWithIcon,
  DiscountLabelRed,
} from './Summary.styles';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';
import { OrderStatus } from 'src/models/enums/OrderStatus.enum';

interface SummaryProps {
  onBack: () => void;
}

interface Address {
  id: string;
  contactPerson: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
}

interface Product {
  id: string;
  name: string;
}

interface Discount {
  id: string;
  name: string;
  percentage: string | null;
  defaultValue: string | null;
}

interface Surcharge {
  id: string;
  name: string;
  percentage: string | null;
  defaultValue: string | null;
}

interface OrderItem {
  productId: string;
  productName?: string;
  quantity: number;
  unitPrice: number;
}

interface SurchargeItem {
  surchargeId: string;
  surchargeName?: string;
  amount: number;
  percentage?: number | null;
}

interface OrderPayload {
  customerId: string;
  customerBillingAddressId: string;
  customerDeliveryAddressId: string;
  scheduledDeliveryDate: string;
  status: string;
  deliveryNotes: string;
  orderItems: {
    productId: string;
    quantity: number;
    unitPrice: number;
  }[];
  orderDiscount?: {
    discountId: string;
    name: string;
    percentage: number | null;
    amount: number | null;
  };
  orderSurcharges?: {
    surchargeId: string;
    name: string;
    percentage: number | null;
    amount: number | null;
  }[];
}

const Summary: FC<SummaryProps> = ({ onBack }) => {
  const [customerName, setCustomerName] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [scheduledDeliveryDate, setScheduledDeliveryDate] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [billingAddress, setBillingAddress] = useState<Address | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState<Address | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountName, setDiscountName] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState<number | null>(
    null
  );
  const [surcharges, setSurcharges] = useState<SurchargeItem[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [allSurcharges, setAllSurcharges] = useState<Surcharge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { showSnackbar } = StyledSnackbar();

  useEffect(() => {
    loadOrderData();
  }, []);

  const loadOrderData = async () => {
    try {
      setIsLoading(true);
      const step1Data = localStorage.getItem('orderStep1');
      if (step1Data) {
        const step1 = JSON.parse(step1Data);

        setCustomerName(step1.customerName || '');

        setOrderDate(
          new Date()
            .toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
            .toUpperCase()
        );
        setScheduledDeliveryDate(
          new Date(step1.scheduledDeliveryDate)
            .toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
            .toUpperCase()
        );
        setDeliveryNote(step1.deliveryNote || '');

        setBillingAddress(step1.billingAddress || null);
        setDeliveryAddress(step1.deliveryAddress || null);
      }

      const step2Data = localStorage.getItem('orderStep2');
      if (step2Data) {
        const step2 = JSON.parse(step2Data);
        setItems(step2.items || []);
      }

      const step3Data = localStorage.getItem('orderStep3');
      if (step3Data) {
        const step3 = JSON.parse(step3Data);
        setApplyDiscount(step3.applyDiscount || false);
        setDiscountAmount(step3.discountAmount || 0);
        setDiscountName(step3.discountName || '');
        setDiscountPercentage(step3.discountPercentage || null);
        setDiscounts(step3.discounts || []);
      }

      const step4Data = localStorage.getItem('orderStep4');
      if (step4Data) {
        const step4 = JSON.parse(step4Data);
        setAllSurcharges(step4.allSurcharges || []);
        setSurcharges(step4.items || []);
      }
      setIsLoading(false);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      setIsLoading(false);
    }
  };

  const itemsSubtotal = items.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice,
    0
  );
  const netTotal = itemsSubtotal - discountAmount;
  const totalSurcharges = surcharges.reduce((sum, s) => sum + s.amount, 0);
  const grandTotal = netTotal + totalSurcharges;

  const handleSave = async () => {
    try {
      const orderPayload = buildOrderPayload('New');
      await axiosTokenInstance.post(apiUrls.CREATE_ORDER_API, orderPayload);
      showSnackbar('Order saved successfully!', 'success');
      clearOrderData();
      setTimeout(() => {
        navigate(routeURL.USER + routeURL.ORDER_MANAGEMENT);
      }, 1500);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleSaveAndComplete = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const orderPayload = buildOrderPayload(OrderStatus.PENDING);
      await axiosTokenInstance.post(apiUrls.CREATE_ORDER_API, orderPayload);
      showSnackbar('Order saved and completed!', 'success');
      clearOrderData();
      setTimeout(() => {
        navigate(routeURL.USER + routeURL.ORDER_MANAGEMENT);
      }, 1500);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      setIsSubmitting(false);
    }
  };

  const buildOrderPayload = (status: string): OrderPayload => {
    const step1Data = localStorage.getItem('orderStep1');
    const step2Data = localStorage.getItem('orderStep2');
    const step3Data = localStorage.getItem('orderStep3');
    const step4Data = localStorage.getItem('orderStep4');

    if (!step1Data || !step2Data) {
      throw new Error('Missing required order data');
    }

    const step1 = JSON.parse(step1Data);
    const step2 = JSON.parse(step2Data);
    const step3 = step3Data ? JSON.parse(step3Data) : null;
    const step4 = step4Data ? JSON.parse(step4Data) : null;

    const formatDate = (dateString: string): string => {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    const payload: OrderPayload = {
      customerId: step1.customerId,
      customerBillingAddressId: step1.billingAddressId,
      customerDeliveryAddressId: step1.deliveryAddressId,
      scheduledDeliveryDate: formatDate(step1.scheduledDeliveryDate),
      status: status,
      deliveryNotes: step1.deliveryNote || '',
      orderItems: step2.items.map((item: OrderItem) => ({
        productId: item.productId,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
      })),
    };

    if (step3 && step3.applyDiscount && step3.discountId) {
      const isPercentageDiscount = step3.discountPercentage != null;
      const percentageValue = isPercentageDiscount
        ? Number(step3.discountPercentage)
        : null;

      payload.orderDiscount = {
        discountId: step3.discountId,
        name: step3.discountName,
        percentage: percentageValue,
        amount:
          percentageValue !== null
            ? null
            : step3.discountAmount != null
            ? Number(step3.discountAmount)
            : null,
      } as any;
    }

    if (step4 && step4.items && step4.items.length > 0) {
      payload.orderSurcharges = step4.items
        .filter((item: SurchargeItem) => item.surchargeId)
        .map((item: SurchargeItem) => {
          const percentageValue =
            (item as any).percentage != null
              ? Number((item as any).percentage)
              : null;

          return {
            surchargeId: item.surchargeId,
            name: item.surchargeName,
            percentage: percentageValue,
            amount:
              percentageValue !== null
                ? null
                : item.amount != null
                ? Number(item.amount)
                : null,
          } as any;
        });
    }

    return payload;
  };

  const clearOrderData = () => {
    localStorage.removeItem('orderStep1');
    localStorage.removeItem('orderStep2');
    localStorage.removeItem('orderStep3');
    localStorage.removeItem('orderStep4');
  };

  const renderAddressCard = (address: Address | null, title: string) => {
    if (!address) return null;

    return (
      <AddressCard>
        <AddressTitle>{title}</AddressTitle>

        <AddressRow>
          <StyledBusinessIcon />
          <AddressName>{address.contactPerson}</AddressName>
        </AddressRow>

        <AddressLinesWrap>
          <AddressLine>{address.addressLine1}</AddressLine>
          <AddressLine>{address.city}</AddressLine>
          <AddressLine>{address.postalCode}</AddressLine>
          <AddressLine>{address.country}</AddressLine>
        </AddressLinesWrap>
      </AddressCard>
    );
  };

  return (
    <SummaryContainer>
      {isLoading ? (
        <LoadingBox>
          <LoadingText>Loading order details...</LoadingText>
        </LoadingBox>
      ) : items.length === 0 ? (
        <NoDataBox>
          <NoDataTitle>No Order Data Found</NoDataTitle>
          <NoDataText>
            Please complete the previous steps to create an order.
          </NoDataText>
        </NoDataBox>
      ) : (
        <>
          <ScrollableContent>
            <TopRowWrapper>
              <LeftColumn>
                <SectionTitle>Customer & Delivery Details</SectionTitle>
                <RowBetween>
                  <ColumnGap8>
                    <SmallLabel14>Customer</SmallLabel14>
                    <SmallValue14>{customerName}</SmallValue14>
                  </ColumnGap8>
                  <ColumnGap8Right>
                    <SmallLabel14>Date</SmallLabel14>
                    <SmallValue14>{orderDate}</SmallValue14>
                  </ColumnGap8Right>
                </RowBetween>
                <DateBlockContainer>
                  <DateBlock>
                    <RowBetween>
                      <ColumnGap8>
                        <SmallLabel14>Scheduled Delivery Date</SmallLabel14>
                        <SmallValue14>{scheduledDeliveryDate}</SmallValue14>
                      </ColumnGap8>
                      <ColumnGap8Right>
                        <SmallLabel14Right>
                          Actual Delivery Date
                        </SmallLabel14Right>
                        <SmallValue14>N/A</SmallValue14>
                      </ColumnGap8Right>
                    </RowBetween>
                    <ColumnGap8>
                      <SmallLabel14>Delivery Note</SmallLabel14>
                      <SmallValue14>
                        <ExpandableText
                          text={deliveryNote}
                          lines={2}
                          placeholder="N/A"
                        />
                      </SmallValue14>
                    </ColumnGap8>
                    <AddressesWrap>
                      {renderAddressCard(billingAddress, 'Billing Address')}
                      {renderAddressCard(deliveryAddress, 'Delivery Address')}
                    </AddressesWrap>
                  </DateBlock>
                </DateBlockContainer>
                <ItemsTitle>Items</ItemsTitle>
                <ItemsContainer>
                  <ItemsHeader>
                    <ItemCell2>Item</ItemCell2>
                    <ItemCell1Right>Quantity</ItemCell1Right>
                    <ItemCell1Right>Unit Price</ItemCell1Right>
                    <ItemCell1Right>Subtotal</ItemCell1Right>
                  </ItemsHeader>
                  {items.length > 5 ? (
                    <ItemsScrollWrapper>
                      {items.map((item, index) => (
                        <ItemRow key={index}>
                          <ItemCell2>{item.productName}</ItemCell2>
                          <ItemCell1Right>
                            {item.quantity.toLocaleString('en-US')}
                          </ItemCell1Right>
                          <ItemCell1Right>
                            {item.unitPrice.toLocaleString('en-US')}
                          </ItemCell1Right>
                          <ItemCell1Right>
                            {(item.quantity * item.unitPrice).toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </ItemCell1Right>
                        </ItemRow>
                      ))}
                    </ItemsScrollWrapper>
                  ) : (
                    <ItemsListContainer>
                      {items.map((item, index) => (
                        <ItemRow key={index}>
                          <ItemCell2>{item.productName}</ItemCell2>
                          <ItemCell1Right>
                            {item.quantity.toLocaleString('en-US')}
                          </ItemCell1Right>
                          <ItemCell1Right>
                            {item.unitPrice.toLocaleString('en-US')}
                          </ItemCell1Right>
                          <ItemCell1Right>
                            {(item.quantity * item.unitPrice).toLocaleString(
                              'en-US',
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </ItemCell1Right>
                        </ItemRow>
                      ))}
                    </ItemsListContainer>
                  )}
                </ItemsContainer>
              </LeftColumn>
              <RightColumn>
                {/* {applyDiscount && (
                  <DiscountsHeaderWrap>
                    <DiscountsRow>
                      <DiscountText>Discounts</DiscountText>
                      <DiscountAppliedRow>
                        <DiscountText>Discount Applied</DiscountText>
                        <StyledCheckCircleIcon />
                      </DiscountAppliedRow>
                    </DiscountsRow>
                    <RowBetween>
                      <DiscountText>Discount Amount</DiscountText>
                      <DiscountAmountLabel>
                        -{' '}
                        {discountAmount.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </DiscountAmountLabel>
                    </RowBetween>
                  </DiscountsHeaderWrap>
                )}
                {surcharges.length > 0 && (
                  <SurchargesBlock>
                    <SurchargesTitle>Surcharges</SurchargesTitle>
                    {surcharges.map((surcharge, index) => (
                      <SurchargeRow key={index}>
                        <SurchargeText>{surcharge.surchargeName}</SurchargeText>
                        <SurchargeValue>
                          {surcharge.amount.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </SurchargeValue>
                      </SurchargeRow>
                    ))}
                  </SurchargesBlock>
                )} */}
                <div>
                  <FinalTotalsHeading>Final Totals</FinalTotalsHeading>
                  <TotalsRow>
                    <TotalsLabel>Items Subtotal</TotalsLabel>
                    <TotalsValueCombined>
                      {itemsSubtotal.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TotalsValueCombined>
                  </TotalsRow>
                  {applyDiscount && discountAmount > 0 && (
                    <TotalsRow>
                      <LabelWithIcon>
                        <DiscountLabelRed>Discounts</DiscountLabelRed>
                        <Tooltip
                          title={
                            <TooltipContainer>
                              <TooltipTitle>Discount Details</TooltipTitle>
                              <TooltipRow>
                                <TooltipLabel>
                                  {discountName || 'Discount'}
                                  {discountPercentage != null &&
                                    ` (${discountPercentage}%)`}
                                </TooltipLabel>
                                <TooltipValue>
                                  {discountAmount.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </TooltipValue>
                              </TooltipRow>
                            </TooltipContainer>
                          }
                          arrow
                          placement="top"
                        >
                          <InfoIconStyled />
                        </Tooltip>
                      </LabelWithIcon>
                      <DiscountLabelRed>
                        -{' '}
                        {discountAmount.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </DiscountLabelRed>
                    </TotalsRow>
                  )}
                  <TotalsRow>
                    <TotalsLabel>Net Total</TotalsLabel>
                    <TotalsValueCombined>
                      {netTotal.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TotalsValueCombined>
                  </TotalsRow>
                  {totalSurcharges > 0 && (
                    <TotalsRowMb2373>
                      <LabelWithIcon>
                        <SurchargeLabelBlue>Surcharges</SurchargeLabelBlue>
                        <Tooltip
                          title={
                            <TooltipContainer>
                              <TooltipTitle>Surcharges Breakdown</TooltipTitle>
                              <TooltipRow
                                sx={{ fontWeight: 600, marginBottom: '4px' }}
                              >
                                <TooltipLabel sx={{ fontWeight: 600 }}>
                                  Name
                                </TooltipLabel>
                                <TooltipValue sx={{ fontWeight: 600 }}>
                                  Amount
                                </TooltipValue>
                              </TooltipRow>
                              {surcharges.map((surcharge, index) => (
                                <TooltipRow key={index}>
                                  <TooltipLabel>
                                    {surcharge.surchargeName}
                                    {surcharge.percentage != null &&
                                      ` (${surcharge.percentage}%)`}
                                  </TooltipLabel>
                                  <TooltipValue>
                                    {surcharge.amount.toLocaleString('en-US', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </TooltipValue>
                                </TooltipRow>
                              ))}
                              <TooltipTotalRow>
                                <TooltipTotalLabel>
                                  Total Amount
                                </TooltipTotalLabel>
                                <TooltipTotalValue>
                                  {totalSurcharges.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </TooltipTotalValue>
                              </TooltipTotalRow>
                            </TooltipContainer>
                          }
                          arrow
                          placement="top"
                        >
                          <InfoIconBlue />
                        </Tooltip>
                      </LabelWithIcon>
                      <SurchargesValue>
                        +{' '}
                        {totalSurcharges.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </SurchargesValue>
                    </TotalsRowMb2373>
                  )}
                  <GrandTotalBox>
                    <TotalsLabel>Grand Total</TotalsLabel>
                    <TotalsValueCombined>
                      {grandTotal.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TotalsValueCombined>
                  </GrandTotalBox>
                </div>
              </RightColumn>
            </TopRowWrapper>
          </ScrollableContent>
          <ActionsWrap>
            <BackButton onClick={onBack}>Back</BackButton>
            {/* <SaveButton onClick={handleSave}>Save</SaveButton> */}
            <SaveCompleteButton
              onClick={handleSaveAndComplete}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
            </SaveCompleteButton>
          </ActionsWrap>
        </>
      )}
    </SummaryContainer>
  );
};

export default Summary;
